import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, ChevronRight, ChevronLeft, Save, Plus, Trash2, Upload, X, Image as ImageIcon, BedDouble, CalendarRange, Ban, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import { compressManyImages } from '../../utils/imageCompress';
import { ROOM_AMENITIES, HOTEL_FACILITIES } from '../../data/hospitalityLists';
import { HourlyPricingEditor } from '../HourlyPricingEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COMMON_AMENITIES = ROOM_AMENITIES;

/**
 * Admin master-editor for ANY hotel.
 * Stage 1 — Admin can edit hotel profile, photos, rooms, inventory, and suspend status.
 */
export const AdminHotelEditor = () => {
  const [hotels, setHotels] = useState([]);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null); // hotel object with rooms/inventory loaded
  const [tab, setTab] = useState('profile');

  const loadList = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/hotels`, { withCredentials: true });
      setHotels(data || []);
    } catch (e) { toast.error('Failed to load hotels'); }
  }, []);

  const loadFull = useCallback(async (hid, resetTab = false) => {
    try {
      const { data } = await axios.get(`${API}/admin/hotels/${hid}/full`, { withCredentials: true });
      setSelected(data);
      if (resetTab) setTab('profile');
    } catch (e) { toast.error('Failed to load hotel details'); }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  const filtered = hotels.filter((h) =>
    !query.trim() ||
    (h.hotel_name || '').toLowerCase().includes(query.toLowerCase()) ||
    (h.city_area || '').toLowerCase().includes(query.toLowerCase()) ||
    (h.email || '').toLowerCase().includes(query.toLowerCase())
  );

  if (selected) {
    return (
      <div className="space-y-4" data-testid="admin-hotel-editor">
        <button
          onClick={() => { setSelected(null); loadList(); }}
          className="inline-flex items-center gap-1 text-sm font-bold text-[#FF0059] hover:underline"
          data-testid="admin-editor-back"
        >
          <ChevronLeft className="h-4 w-4" /> Back to hotel list
        </button>

        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <h2 className="text-2xl font-black tracking-tight text-black">{selected.hotel_name}</h2>
              <p className="text-sm text-slate-600">{selected.hotel_address} · {selected.city_area}</p>
              <p className="text-xs text-slate-400 mt-1">{selected.email} · {selected.phone || '—'}</p>
            </div>
            <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase ${
              selected.approval_status === 'suspended' ? 'bg-red-100 text-red-700' :
              selected.approval_status === 'approved' ? 'bg-green-100 text-green-700' :
              'bg-yellow-100 text-yellow-800'
            }`} data-testid="hotel-edit-status">
              {selected.approval_status}
            </span>
          </div>

          {/* Sub-tabs */}
          <div className="flex flex-wrap gap-1 border-b border-slate-200 mb-5" data-testid="admin-editor-subtabs">
            {[
              { id: 'profile', label: 'Profile' },
              { id: 'photos', label: 'Photos' },
              { id: 'rooms', label: 'Rooms' },
              { id: 'inventory', label: 'Inventory & Rates' },
              { id: 'status', label: 'Status' },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-subtab-${t.id}`}
                className={`px-4 py-2 text-sm font-bold border-b-2 transition-colors -mb-px ${
                  tab === t.id ? 'border-[#FF0059] text-[#FF0059]' : 'border-transparent text-slate-500 hover:text-black'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'profile' && <ProfileTab hotel={selected} reload={() => loadFull(selected._id)} />}
          {tab === 'photos' && <PhotosTab hotel={selected} reload={() => loadFull(selected._id)} />}
          {tab === 'rooms' && <RoomsTab hotel={selected} reload={() => loadFull(selected._id)} />}
          {tab === 'inventory' && <InventoryTab hotel={selected} reload={() => loadFull(selected._id)} />}
          {tab === 'status' && <StatusTab hotel={selected} reload={() => loadFull(selected._id)} />}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-hotel-editor-list">
      <div className="mb-5 flex items-center gap-2">
        <Search className="h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search hotel by name, city or email..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="flex-1 px-3 py-2.5 rounded-full border border-gray-200 focus:border-[#FF0059] outline-none text-sm"
          data-testid="admin-hotel-search"
        />
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left text-xs font-bold text-slate-600 uppercase">
              <th className="px-4 py-3">Hotel</th>
              <th className="px-4 py-3">City</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan="4" className="px-4 py-10 text-center text-slate-500">No hotels match.</td></tr>
            ) : filtered.map((h) => (
              <tr key={h._id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`admin-hotel-row-${h._id}`}>
                <td className="px-4 py-3 font-bold text-black">{h.hotel_name}</td>
                <td className="px-4 py-3 text-slate-600">{h.city_area || '—'}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                    h.approval_status === 'suspended' ? 'bg-red-100 text-red-700' :
                    h.approval_status === 'approved' ? 'bg-green-100 text-green-700' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>{h.approval_status || 'pending'}</span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => loadFull(h._id, true)}
                    className="inline-flex items-center gap-1 text-xs font-bold text-[#FF0059] hover:underline"
                    data-testid={`admin-edit-hotel-${h._id}`}
                  >
                    Edit <ChevronRight className="h-3 w-3" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ===========================================================================
// PROFILE TAB
// ===========================================================================
const ProfileTab = ({ hotel, reload }) => {
  const [form, setForm] = useState({
    hotel_name: hotel.hotel_name || '',
    hotel_address: hotel.hotel_address || '',
    city_area: hotel.city_area || '',
    description: hotel.description || '',
    hotel_category: hotel.hotel_category || '3-star',
    commission_rate: hotel.commission_rate || 20,
    contact_email: hotel.contact_email || hotel.email || '',
    contact_phone: hotel.contact_phone || hotel.phone || '',
    check_in_time: hotel.check_in_time || '14:00',
    check_out_time: hotel.check_out_time || '11:00',
    facilities: Array.isArray(hotel.facilities) ? hotel.facilities : [],
  });
  const [saving, setSaving] = useState(false);

  const toggleFacility = (f) => {
    setForm((p) => ({
      ...p,
      facilities: p.facilities.includes(f)
        ? p.facilities.filter((x) => x !== f)
        : [...p.facilities, f],
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/hotels/${hotel._id}/profile`, form, { withCredentials: true });
      toast.success('Profile saved');
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid="admin-profile-tab">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <FieldText label="Hotel name" value={form.hotel_name} onChange={(v) => setForm({ ...form, hotel_name: v })} testId="admin-profile-name" />
        <FieldText label="City / Area" value={form.city_area} onChange={(v) => setForm({ ...form, city_area: v })} testId="admin-profile-city" />
      </div>
      <FieldText label="Full address" value={form.hotel_address} onChange={(v) => setForm({ ...form, hotel_address: v })} testId="admin-profile-address" />
      <FieldTextarea label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} testId="admin-profile-desc" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <FieldSelect
          label="Category"
          value={form.hotel_category}
          onChange={(v) => setForm({ ...form, hotel_category: v })}
          options={['1-star', '2-star', '3-star', '4-star', '5-star']}
          testId="admin-profile-category"
        />
        <FieldNumber label="Commission %" value={form.commission_rate} onChange={(v) => setForm({ ...form, commission_rate: v })} testId="admin-profile-commission" min={0} max={50} step={1} />
        <FieldText label="Contact email" value={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} testId="admin-profile-email" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <FieldText label="Contact phone" value={form.contact_phone} onChange={(v) => setForm({ ...form, contact_phone: v })} testId="admin-profile-phone" />
        <FieldText label="Check-in time" type="time" value={form.check_in_time} onChange={(v) => setForm({ ...form, check_in_time: v })} testId="admin-profile-checkin" />
        <FieldText label="Check-out time" type="time" value={form.check_out_time} onChange={(v) => setForm({ ...form, check_out_time: v })} testId="admin-profile-checkout" />
      </div>
      <div data-testid="admin-profile-facilities">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">
          Hotel facilities <span className="text-slate-400">({form.facilities.length}/{HOTEL_FACILITIES.length})</span>
        </p>
        <div className="flex flex-wrap gap-1.5">
          {HOTEL_FACILITIES.map((f) => {
            const on = form.facilities.includes(f);
            return (
              <button
                key={f}
                type="button"
                onClick={() => toggleFacility(f)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-bold border-2 ${on ? 'bg-[#FF0059] text-white border-[#FF0059]' : 'bg-white text-slate-700 border-slate-200'}`}
                data-testid={`admin-facility-${f.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
              >
                {f}
              </button>
            );
          })}
        </div>
      </div>
      <Button onClick={save} disabled={saving} className="bg-[#FF0059] hover:bg-[#CC0047] text-white font-bold rounded-full" data-testid="admin-profile-save">
        <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving…' : 'Save profile'}
      </Button>
    </div>
  );
};

// ===========================================================================
// PHOTOS TAB
// ===========================================================================
const PhotosTab = ({ hotel, reload }) => {
  const [images, setImages] = useState(hotel.images || []);
  const [uploading, setUploading] = useState(false);

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const out = await compressManyImages(Array.from(files), { targetKb: 200 });
      setImages([...images, ...out.map((c) => c.dataUrl).filter(Boolean)]);
    } catch { toast.error('Upload failed'); } finally { setUploading(false); }
  };

  const save = async () => {
    try {
      await axios.put(`${API}/admin/hotels/${hotel._id}/images`, { images }, { withCredentials: true });
      toast.success(`Saved ${images.length} photos`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  return (
    <div data-testid="admin-photos-tab">
      <label className="flex items-center justify-center gap-2 px-4 py-3 mb-3 rounded-xl border-2 border-dashed border-gray-300 hover:border-[#FF0059] cursor-pointer">
        <Upload className="h-4 w-4" />
        <span className="text-sm font-bold">{uploading ? 'Compressing…' : 'Click to upload hotel photos'}</span>
        <input type="file" multiple accept="image/*" className="hidden" onChange={(e) => { upload(e.target.files); e.target.value = ''; }} data-testid="admin-photos-input" />
      </label>
      {images.length > 0 && (
        <div className="grid grid-cols-3 md:grid-cols-5 gap-2 mb-4" data-testid="admin-photos-grid">
          {images.map((src, idx) => (
            <div key={`${idx}-${(src || '').slice(-16)}`} className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 group">
              <img src={src} alt="" className="w-full h-full object-cover" />
              <button onClick={() => setImages(images.filter((_, i) => i !== idx))} className="absolute top-1 right-1 p-1 rounded-full bg-white/90 hover:bg-red-500 hover:text-white" data-testid={`admin-photo-remove-${idx}`}>
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      <Button onClick={save} className="bg-[#FF0059] hover:bg-[#CC0047] text-white font-bold rounded-full" data-testid="admin-photos-save">
        <Save className="h-4 w-4 mr-1" /> Save photos
      </Button>
    </div>
  );
};

// ===========================================================================
// ROOMS TAB
// ===========================================================================
const RoomsTab = ({ hotel, reload }) => {
  const [editing, setEditing] = useState(null);
  const rooms = hotel.room_types || [];

  const open = (r) => setEditing(r ? { ...r, amenities: r.amenities || [], images: r.images || [], hourly_slots: r.hourly_slots || [] } : {
    name: '', description: '', base_price: 1500, capacity: 2, total_rooms: 1,
    amenities: ['Air Conditioning', 'Free WiFi'], images: [],
    extra_adult_price: 0, extra_child_price: 0,
    hourly_pricing_enabled: false, hourly_slots: [],
  });
  const close = () => setEditing(null);

  const save = async () => {
    if (!editing.name.trim()) return toast.error('Name required');
    const body = {
      name: editing.name, description: editing.description || '',
      base_price: Number(editing.base_price) || 0, capacity: Number(editing.capacity) || 1,
      total_rooms: Number(editing.total_rooms) || 1, amenities: editing.amenities, images: editing.images,
      extra_adult_price: Number(editing.extra_adult_price) || 0,
      extra_child_price: Number(editing.extra_child_price) || 0,
      hourly_pricing_enabled: !!editing.hourly_pricing_enabled,
      hourly_slots: editing.hourly_slots || [],
    };
    try {
      if (editing._id) {
        await axios.put(`${API}/admin/hotels/${hotel._id}/rooms/${editing._id}`, body, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/hotels/${hotel._id}/rooms`, body, { withCredentials: true });
      }
      toast.success('Room saved');
      close(); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete "${r.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/hotels/${hotel._id}/rooms/${r._id}`, { withCredentials: true });
      toast.success('Deleted');
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const uploadImages = async (files) => {
    if (!files || files.length === 0) return;
    try {
      const out = await compressManyImages(Array.from(files), { targetKb: 200 });
      setEditing({ ...editing, images: [...editing.images, ...out.map((c) => c.dataUrl).filter(Boolean)] });
    } catch { toast.error('Upload failed'); }
  };

  return (
    <div data-testid="admin-rooms-tab">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-slate-600">{rooms.length} room type{rooms.length === 1 ? '' : 's'}</p>
        <Button onClick={() => open(null)} className="bg-[#FF0059] hover:bg-[#CC0047] text-white font-bold rounded-full" data-testid="admin-room-add">
          <Plus className="h-4 w-4 mr-1" /> Add room type
        </Button>
      </div>
      {rooms.length === 0 ? (
        <div className="text-center py-10 bg-slate-50 rounded-xl">
          <BedDouble className="h-10 w-10 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">No rooms yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="admin-rooms-list">
          {rooms.map((r) => (
            <div key={r._id} className="bg-white border border-slate-200 rounded-xl overflow-hidden flex" data-testid={`admin-room-card-${r._id}`}>
              <div className="w-32 h-32 bg-slate-100 flex-shrink-0">
                {r.images && r.images[0] ? <img src={r.images[0]} alt={r.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-300 text-xs">No photo</div>}
              </div>
              <div className="p-3 flex-1 flex flex-col">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-black text-sm">{r.name}</p>
                    <p className="text-xs text-slate-500">Cap {r.capacity} · {r.total_rooms || 1} rooms · {(r.images || []).length} photos</p>
                  </div>
                  <p className="text-base font-black whitespace-nowrap">₹{Number(r.base_price).toLocaleString('en-IN')}</p>
                </div>
                <div className="mt-auto flex gap-2 pt-2">
                  <Button onClick={() => open(r)} variant="outline" className="flex-1 font-bold rounded-full" data-testid={`admin-room-edit-${r._id}`}>Edit</Button>
                  <Button onClick={() => del(r)} variant="outline" className="font-bold rounded-full border-red-200 text-red-600 hover:bg-red-50" data-testid={`admin-room-delete-${r._id}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={close} data-testid="admin-room-modal">
          <div className="bg-white rounded-3xl max-w-xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between mb-4">
              <h3 className="text-xl font-black">{editing._id ? 'Edit room' : 'Add room'}</h3>
              <button onClick={close} className="p-1 rounded-full hover:bg-slate-100"><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-3">
              <FieldText label="Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testId="admin-room-name" />
              <FieldTextarea label="Description" value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} testId="admin-room-desc" />
              <div className="grid grid-cols-3 gap-2">
                <FieldNumber label="₹/night" value={editing.base_price} onChange={(v) => setEditing({ ...editing, base_price: v })} testId="admin-room-price" min={100} step={50} />
                <FieldNumber label="Capacity" value={editing.capacity} onChange={(v) => setEditing({ ...editing, capacity: v })} testId="admin-room-cap" min={1} max={12} step={1} />
                <FieldNumber label="Total rooms" value={editing.total_rooms} onChange={(v) => setEditing({ ...editing, total_rooms: v })} testId="admin-room-total" min={1} max={500} step={1} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <FieldNumber label="Extra adult (₹/night)" value={editing.extra_adult_price ?? 0} onChange={(v) => setEditing({ ...editing, extra_adult_price: v })} testId="admin-room-extra-adult" min={0} step={50} />
                <FieldNumber label="Extra child (₹/night)" value={editing.extra_child_price ?? 0} onChange={(v) => setEditing({ ...editing, extra_child_price: v })} testId="admin-room-extra-child" min={0} step={50} />
              </div>
              <HourlyPricingEditor
                enabled={!!editing.hourly_pricing_enabled}
                slots={editing.hourly_slots || []}
                onChange={({ enabled: en, slots: sl }) => setEditing({ ...editing, hourly_pricing_enabled: en, hourly_slots: sl })}
                testIdPrefix="admin-hourly"
              />
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Amenities</p>
                <div className="flex flex-wrap gap-1.5">
                  {COMMON_AMENITIES.map((a) => {
                    const on = editing.amenities.includes(a);
                    return (
                      <button key={a} type="button" onClick={() => setEditing({ ...editing, amenities: on ? editing.amenities.filter((x) => x !== a) : [...editing.amenities, a] })}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-bold border-2 ${on ? 'bg-[#FF0059] text-white border-[#FF0059]' : 'bg-white text-slate-700 border-slate-200'}`}>
                        {a}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Photos ({editing.images.length})</p>
                <label className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg border-2 border-dashed border-slate-300 hover:border-[#FF0059] cursor-pointer">
                  <ImageIcon className="h-4 w-4" />
                  <span className="text-xs font-bold">Click to upload</span>
                  <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { uploadImages(e.target.files); e.target.value = ''; }} />
                </label>
                {editing.images.length > 0 && (
                  <div className="grid grid-cols-4 gap-1.5 mt-2">
                    {editing.images.map((src, idx) => (
                      <div key={`${idx}-${(src || '').slice(-16)}`} className="relative aspect-square rounded overflow-hidden bg-slate-100">
                        <img src={src} alt="" className="w-full h-full object-cover" />
                        <button onClick={() => setEditing({ ...editing, images: editing.images.filter((_, i) => i !== idx) })} className="absolute top-0.5 right-0.5 p-0.5 rounded-full bg-white/90 hover:bg-red-500 hover:text-white">
                          <X className="h-2.5 w-2.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-2 mt-4 pt-3 border-t">
              <Button onClick={close} variant="outline" className="flex-1 rounded-full font-bold">Cancel</Button>
              <Button onClick={save} className="flex-1 bg-[#FF0059] hover:bg-[#CC0047] text-white font-bold rounded-full" data-testid="admin-room-save">Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ===========================================================================
// INVENTORY TAB
// ===========================================================================
const InventoryTab = ({ hotel, reload }) => {
  const rooms = hotel.room_types || [];
  const [form, setForm] = useState({
    room_type_id: rooms[0]?._id || '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0],
    price: '',
    available: '',
    weekdays_only: false,
    weekends_only: false,
    closed: '',  // '' = no change, 'close' = close out, 'open' = re-open
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.room_type_id) return toast.error('Select a room');
    if (!form.price && !form.available && !form.closed) return toast.error('Provide price, availability, or open/close');
    setSubmitting(true);
    try {
      const body = {
        room_type_id: form.room_type_id,
        start_date: form.start_date,
        end_date: form.end_date,
        weekdays_only: form.weekdays_only,
        weekends_only: form.weekends_only,
      };
      if (form.price) body.price = Number(form.price);
      if (form.available) body.available = Number(form.available);
      if (form.closed === 'close') body.closed = true;
      else if (form.closed === 'open') body.closed = false;
      const { data } = await axios.put(`${API}/admin/hotels/${hotel._id}/inventory/bulk`, body, { withCredentials: true });
      toast.success(data.message);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
    finally { setSubmitting(false); }
  };

  if (rooms.length === 0) {
    return (
      <div className="text-center py-10 bg-slate-50 rounded-xl" data-testid="admin-inventory-empty">
        <CalendarRange className="h-10 w-10 text-slate-300 mx-auto mb-2" />
        <p className="text-sm text-slate-500">Add at least one room type first.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="admin-inventory-tab">
      <FieldSelect label="Room type" value={form.room_type_id} onChange={(v) => setForm({ ...form, room_type_id: v })}
        options={rooms.map((r) => ({ value: r._id, label: `${r.name} — ₹${r.base_price}` }))} testId="admin-inv-room" />
      <div className="grid grid-cols-2 gap-3">
        <FieldText label="Start date" type="date" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} testId="admin-inv-start" />
        <FieldText label="End date" type="date" value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} testId="admin-inv-end" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <FieldNumber label="New price (₹) — optional" value={form.price} onChange={(v) => setForm({ ...form, price: v })} testId="admin-inv-price" min={0} step={100} />
        <FieldNumber label="Available rooms — optional" value={form.available} onChange={(v) => setForm({ ...form, available: v })} testId="admin-inv-avail" min={0} step={1} />
      </div>
      <div className="flex flex-wrap gap-3 items-center">
        <label className="flex items-center gap-2 text-xs font-bold">
          <input type="checkbox" checked={form.weekdays_only} onChange={(e) => setForm({ ...form, weekdays_only: e.target.checked, weekends_only: false })} data-testid="admin-inv-weekdays" /> Weekdays only
        </label>
        <label className="flex items-center gap-2 text-xs font-bold">
          <input type="checkbox" checked={form.weekends_only} onChange={(e) => setForm({ ...form, weekends_only: e.target.checked, weekdays_only: false })} data-testid="admin-inv-weekends" /> Weekends only
        </label>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Open/Close:</span>
          <select
            value={form.closed}
            onChange={(e) => setForm({ ...form, closed: e.target.value })}
            className="px-2 py-1 rounded-lg border border-slate-200 focus:border-[#FF0059] outline-none text-xs font-bold"
            data-testid="admin-inv-close-select"
          >
            <option value="">No change</option>
            <option value="close">Close rooms (stop sales)</option>
            <option value="open">Re-open rooms</option>
          </select>
        </div>
      </div>
      <Button type="submit" disabled={submitting} className="bg-[#FF0059] hover:bg-[#CC0047] text-white font-bold rounded-full" data-testid="admin-inv-submit">
        <Save className="h-4 w-4 mr-1" /> {submitting ? 'Updating…' : 'Apply'}
      </Button>
    </form>
  );
};

// ===========================================================================
// STATUS TAB (suspend / reinstate)
// ===========================================================================
const StatusTab = ({ hotel, reload }) => {
  const [reason, setReason] = useState('');
  const isSuspended = hotel.approval_status === 'suspended';

  const toggle = async () => {
    try {
      const { data } = await axios.put(`${API}/admin/hotels/${hotel._id}/suspend`,
        { suspended: !isSuspended, reason },
        { withCredentials: true });
      toast.success(data.message);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
  };

  return (
    <div className="space-y-3" data-testid="admin-status-tab">
      <div className={`p-4 rounded-xl border ${isSuspended ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
        <p className="font-black text-sm">{isSuspended ? '⛔ This hotel is suspended' : '✅ This hotel is live'}</p>
        {hotel.suspend_reason && isSuspended && <p className="text-xs text-slate-600 mt-1">Reason: {hotel.suspend_reason}</p>}
        <p className="text-xs text-slate-600 mt-1">{isSuspended ? 'Hidden from public search results.' : 'Visible to all guests in search.'}</p>
      </div>
      {!isSuspended && (
        <FieldText label="Suspension reason (optional)" value={reason} onChange={setReason} testId="admin-status-reason" />
      )}
      <Button onClick={toggle} className={`font-bold rounded-full text-white ${isSuspended ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`} data-testid="admin-status-toggle">
        {isSuspended ? <><CheckCircle2 className="h-4 w-4 mr-1" /> Reinstate</> : <><Ban className="h-4 w-4 mr-1" /> Suspend</>}
      </Button>
    </div>
  );
};

// ===========================================================================
// Field primitives
// ===========================================================================
const FieldText = ({ label, value, onChange, testId, type = 'text' }) => (
  <div>
    <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 block mb-1">{label}</label>
    <input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:border-[#FF0059] outline-none text-sm" data-testid={testId} />
  </div>
);
const FieldNumber = ({ label, value, onChange, testId, min, max, step }) => (
  <div>
    <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 block mb-1">{label}</label>
    <input type="number" value={value} onChange={(e) => onChange(e.target.value)} min={min} max={max} step={step} className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:border-[#FF0059] outline-none text-sm font-bold" data-testid={testId} />
  </div>
);
const FieldTextarea = ({ label, value, onChange, testId }) => (
  <div>
    <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 block mb-1">{label}</label>
    <textarea rows={3} value={value || ''} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:border-[#FF0059] outline-none text-sm" data-testid={testId} />
  </div>
);
const FieldSelect = ({ label, value, onChange, options, testId }) => {
  const opts = options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
  return (
    <div>
      <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 block mb-1">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:border-[#FF0059] outline-none text-sm font-bold" data-testid={testId}>
        {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
};
